import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaBook, FaVideo, FaPlay, FaUserTie, FaSignOutAlt, FaCalendarAlt, FaClock, FaExclamationCircle } from 'react-icons/fa';

const StudentDashboard = () => {
  const [contentAccess, setContentAccess] = useState([]);
  const [contentDetails, setContentDetails] = useState({});
  const [batchDetails, setBatchDetails] = useState({});
  const [teachersMap, setTeachersMap] = useState({});
  const [liveClasses, setLiveClasses] = useState({});
  const [recordings, setRecordings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessCount, setAccessCount] = useState(0);
  const [combinedClasses, setCombinedClasses] = useState([]);
  
  const navigate = useNavigate();
  
  const studentId = localStorage.getItem('student_id');
  const studentName = localStorage.getItem('name');
  
  useEffect(() => {
    if (!studentId) {
      navigate('/login');
      return;
    }
    
    const fetchStudentData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch student's content access
        console.log(`Fetching content access for student ${studentId}`);
        const accessResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/StudentContentAccess/student/${studentId}`);
        console.log('Content access response:', accessResponse.data);
        
        if (!accessResponse.data || !accessResponse.data.data) {
          setContentAccess([]);
          setAccessCount(0);
          setIsLoading(false);
          return;
        }
        
        setContentAccess(accessResponse.data.data || []);
        setAccessCount(accessResponse.data.count || accessResponse.data.data.length || 0);
        
        if (accessResponse.data.data.length === 0) {
          setIsLoading(false);
          return;
        }
        
        // Process each content access to get content details
        const contentDetailsPromises = accessResponse.data.data.map(access => 
          axios.get(`https://apissc.sainikschoolcadet.com/api/contents/${access.content_id}`)
            .catch(err => {
              console.error(`Error fetching content ${access.content_id}:`, err);
              return { data: null };
            })
        );
        
        const contentResponses = await Promise.all(contentDetailsPromises);
        
        // Create a map of content details
        const contentDetailsMap = {};
        contentResponses.forEach(response => {
          if (response && response.data) {
            const content = response.data;
            contentDetailsMap[content.content_id] = content;
          }
        });
        
        console.log('Content details map:', contentDetailsMap);
        setContentDetails(contentDetailsMap);
        
        // For live classes, fetch batch information
        const liveBatchMap = {};
        const teachersData = {};
        const liveClassesData = {};
        const recordingsData = {};
        
        // Create a comprehensive teacher database to track ALL teachers
        const allTeachersMap = {};
        
        // Helper function to extract subject from title
        const extractSubject = (title) => {
          if (!title) return 'N/A';
          
          const titleLower = title.toLowerCase();
          
          // Common subject keywords
          if (titleLower.includes('math') || titleLower.includes('equation')) return 'Maths';
          if (titleLower.includes('english') || titleLower.includes('adjective') || titleLower.includes('grammar')) return 'English';
          if (titleLower.includes('sst') || titleLower.includes('country') || titleLower.includes('british') || titleLower.includes('rule')) return 'SST';
          if (titleLower.includes('science') || titleLower.includes('physics') || titleLower.includes('chemistry')) return 'Science';
          if (titleLower.includes('hindi')) return 'Hindi';
          
          return 'N/A';
        };
        
        for (const access of accessResponse.data.data) {
          // Only process live_class content types
          if (contentDetailsMap[access.content_id]?.content_type === 'live_class') {
            const contentId = access.content_id;
            
            console.log(`Fetching batches for content ${contentId}`);
            try {
              // Get all batches for this content
              const batchesResponse = await axios.get(`https://apissc.sainikschoolcadet.com/api/Batches/content/${contentId}`);
              console.log(`Batches for content ${contentId}:`, batchesResponse.data);
              
              if (Array.isArray(batchesResponse.data) && batchesResponse.data.length > 0) {
                // Check each batch to see if student is enrolled
                for (const batch of batchesResponse.data) {
                  try {
                    const batchId = batch.batch_id;
                    console.log(`Checking if student ${studentId} is in batch ${batchId}`);
                    
                    const enrollmentResponse = await axios.get(
                      `https://apissc.sainikschoolcadet.com/api/BatchStudents/check/${studentId}/${batchId}`
                    );
                    
                    console.log(`Enrollment check result:`, enrollmentResponse.data);
                    
                    // Only process batches where the student is enrolled
                    if (enrollmentResponse.data.is_in_batch) {
                      // Store batch information
                      liveBatchMap[contentId] = {
                        ...batch,
                        enrollment: enrollmentResponse.data
                      };
                      
                      // Fetch teachers for this batch
                      console.log(`Fetching teachers for batch ${batchId}`);
                      const teachersResponse = await axios.get(
                        `https://apissc.sainikschoolcadet.com/api/BatchTeachers/batch/${batchId}/teachers`
                      );
                      
                      const teacherIds = teachersResponse.data.teacher_ids || [];
                      const teacherDetailsPromises = teacherIds.map(teacherId => 
                        axios.get(`https://apissc.sainikschoolcadet.com/api/users/getbyid/${teacherId}`)
                          .catch(err => {
                            console.error(`Error fetching teacher ${teacherId}:`, err);
                            return { data: null };
                          })
                      );
                      
                      const teacherResponses = await Promise.all(teacherDetailsPromises);
                      teachersData[batchId] = teacherResponses
                        .filter(response => response && response.data)
                        .map(response => {
                          const teacherData = {
                            ...response.data,
                            teacher_id: response.data.u_id // Ensure we have teacher_id for mapping
                          };
                          
                          // Add to comprehensive teacher map
                          allTeachersMap[response.data.u_id] = teacherData;
                          
                          return teacherData;
                        });
                      
                      // Fetch live classes for this batch
                      console.log(`Fetching live classes for batch ${batchId}`);
                      const liveClassesResponse = await axios.get(
                        `https://apissc.sainikschoolcadet.com/api/LiveClasses/batch/${batchId}`
                      );
                      
                      liveClassesData[batchId] = liveClassesResponse.data.liveClasses || [];
                      
                      // Fetch recordings for this batch
                      console.log(`Fetching recordings for batch ${batchId}`);
                      const recordingsResponse = await axios.get(
                        `https://apissc.sainikschoolcadet.com/api/LiveClasses/batch-recordings/${batchId}`
                      );
                      
                      recordingsData[batchId] = recordingsResponse.data.recordings || [];
                      
                      break; // Once we find an enrolled batch, we can stop checking others
                    }
                  } catch (err) {
                    console.error(`Error checking enrollment for batch ${batch.batch_id}:`, err);
                  }
                }
              }
            } catch (err) {
              console.error(`Error fetching batches for content ${contentId}:`, err);
            }
          }
        }
        
        // Collect all unique teacher IDs from live classes and recordings
        const uniqueTeacherIds = new Set();
        
        // Check live classes for any teachers not in our database
        Object.entries(liveClassesData).forEach(([batchId, classes]) => {
          classes.forEach(cls => {
            if (cls.teacher_id && !allTeachersMap[cls.teacher_id]) {
              uniqueTeacherIds.add(cls.teacher_id);
            }
          });
        });
        
        // Check recordings for any teachers not in our database
        Object.entries(recordingsData).forEach(([batchId, recordings]) => {
          recordings.forEach(rec => {
            if (rec.teacher_id && !allTeachersMap[rec.teacher_id]) {
              uniqueTeacherIds.add(rec.teacher_id);
            }
          });
        });
        
        // Fetch details for any teachers not already in our database
        if (uniqueTeacherIds.size > 0) {
          console.log(`Fetching details for ${uniqueTeacherIds.size} additional teachers`);
          
          const additionalTeacherPromises = Array.from(uniqueTeacherIds).map(teacherId => 
            axios.get(`https://apissc.sainikschoolcadet.com/api/users/getbyid/${teacherId}`)
              .then(response => {
                if (response && response.data) {
                  allTeachersMap[response.data.u_id] = {
                    ...response.data,
                    teacher_id: response.data.u_id
                  };
                }
              })
              .catch(err => {
                console.error(`Error fetching additional teacher ${teacherId}:`, err);
              })
          );
          
          await Promise.all(additionalTeacherPromises);
        }
        
        setBatchDetails(liveBatchMap);
        setTeachersMap(teachersData);
        setLiveClasses(liveClassesData);
        setRecordings(recordingsData);
        
        // Combine live classes and recordings into a single array
        const combined = [];
        
        // Process all batches
        Object.entries(liveClassesData).forEach(([batchId, classes]) => {
          const batchTeachers = teachersData[batchId] || [];
          const batchRecordings = recordingsData[batchId] || [];
          
          // Add live classes with their recordings if available
          classes.forEach(cls => {
            // First check if teacher is in our comprehensive map
            let instructorName = 'N/A';
            
            if (cls.teacher_id && allTeachersMap[cls.teacher_id]) {
              // Use teacher from comprehensive map if available
              instructorName = allTeachersMap[cls.teacher_id].f_name;
            } else {
              // Fall back to batch teachers if not found in comprehensive map
              const teacher = batchTeachers.find(t => t.u_id === cls.teacher_id) || batchTeachers[0];
              instructorName = teacher ? teacher.f_name : 'N/A';
            }
            
            // Find matching recording for this live class using liveClassId
            const matchingRecording = batchRecordings.find(rec => 
              rec.liveClassId === cls.live_class_id
            );
            
            combined.push({
              id: cls.live_class_id,
              title: cls.title,
              dateTime: cls.start_time,
              endTime: cls.end_time,
              subject: cls.subject || extractSubject(cls.title),
              instructor: instructorName,
              status: cls.status === 'ended' ? 'RECORDED' : 'LIVE',
              type: 'live',
              batchId,
              liveClassId: cls.live_class_id,
              recordings: matchingRecording ? matchingRecording.recordings : []
            });
          });
          
          // Add standalone recordings (those that don't have a matching live class)
          batchRecordings.forEach(recording => {
            const hasMatchingLiveClass = classes.some(cls => 
              recording.liveClassId === cls.live_class_id
            );
            
            if (!hasMatchingLiveClass) {
              // First check if teacher is in our comprehensive map
              let instructorName = 'N/A';
              
              if (recording.teacher_id && allTeachersMap[recording.teacher_id]) {
                // Use teacher from comprehensive map if available
                instructorName = allTeachersMap[recording.teacher_id].f_name;
              } else {
                // Fall back to batch teachers if not found in comprehensive map
                const teacher = batchTeachers.find(t => t.u_id === recording.teacher_id) || batchTeachers[0];
                instructorName = teacher ? teacher.f_name : 'N/A';
              }
              
              // Use the actual recording creation time if available, otherwise fall back to startTime
              const displayTime = recording.recordings && recording.recordings.length > 0 
                ? recording.recordings[0].createdAt 
                : recording.startTime;
              
              combined.push({
                id: `rec-${recording.id || Math.random()}`,
                title: recording.title,
                dateTime: displayTime,
                endTime: recording.endTime,
                subject: recording.subject || extractSubject(recording.title),
                instructor: instructorName,
                status: 'RECORDED',
                type: 'recording',
                batchId,
                recordings: recording.recordings || []
              });
            }
          });
        });
        
        // Sort by date (newest first)
        combined.sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
        
        setCombinedClasses(combined);
        
        console.log('Live batch map:', liveBatchMap);
        console.log('Teachers data:', teachersData);
        console.log('All teachers map:', allTeachersMap);
        console.log('Live classes data:', liveClassesData);
        console.log('Recordings data:', recordingsData);
        console.log('Combined classes:', combined);
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching student data:", err);
        setError("We couldn't load your course content right now. Please try again later.");
        setIsLoading(false);
      }
    };
    
    fetchStudentData();
  }, [studentId, navigate]);
  
  const handleJoinLiveClass = async (liveClassId) => {
    try {
      const response = await axios.get(
        `https://apissc.sainikschoolcadet.com/api/LiveClasses/join-student/${liveClassId}?username=${studentName}`
      );
      
      if (response.data && response.data.joinUrl) {
        window.open(response.data.joinUrl, '_blank');
      } else {
        alert('Unable to join class. Please try again.');
      }
    } catch (err) {
      console.error("Error joining live class:", err);
      alert('Failed to join live class. Please try again later.');
    }
  };
  
  const handleLogout = () => {
    // Clear all localStorage items
    localStorage.removeItem('student_id');
    localStorage.removeItem('role');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    
    // Force a complete page reload and redirect to login
    window.location.href = '/login';
  };
  
  // Format date to "Month DD, YYYY"
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    
    // Check if this looks like it's already in IST by examining the time
    // If the .000Z format is used but it's actually IST data, we don't add offset
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      timeZone: 'UTC', // Treat as-is since data might already be in IST
    });
  };
  
  // Format time to "HH:MM AM/PM"
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    
    // Check if this looks like it's already in IST by examining the time
    // If the .000Z format is used but it's actually IST data, we don't add offset
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC', // Treat as-is since data might already be in IST
    });
  };
  
  // Check if a class is live now
  const isLiveNow = (startTime, endTime) => {
    const now = new Date();
    const start = new Date(startTime);
    const end = new Date(endTime);
    return now >= start && now <= end;
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  // Filter content by type (just live classes)
  const liveClassContents = contentAccess.filter(
    access => contentDetails[access.content_id]?.content_type === 'live_class'
  );
  
  // Get all enrolled batch IDs
  const enrolledBatchIds = Object.values(batchDetails).map(batch => batch.batch_id);
  
  // Collect all teachers across all batches
  const allTeachers = [];
  Object.values(teachersMap).forEach(teachers => {
    teachers.forEach(teacher => {
      if (!allTeachers.some(t => t.u_id === teacher.u_id)) {
        allTeachers.push(teacher);
      }
    });
  });
  
  // If no content is available at all, show a friendly message
  if (liveClassContents.length === 0 || enrolledBatchIds.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="px-6 py-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Sainik School Cadet</h1>
              <p className="text-gray-600">Welcome, {studentName}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
            >
              <FaSignOutAlt className="mr-2" />
              Logout
            </button>
          </div>
        </header>
        
        <div className="px-4 py-4">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <FaBook className="text-5xl text-blue-300 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">No Live Classes Available</h2>
            <p className="text-gray-600 mb-6">You don't have access to any live classes yet.</p>
            <p className="text-sm text-gray-500">Please contact support if you believe this is an error.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
                  <div className="px-6 py-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Sainik School Cadet</h1>
            <p className="text-gray-600">Welcome, {studentName}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
          >
            <FaSignOutAlt className="mr-2" />
            Logout
          </button>
        </div>
      </header>
      
      {/* Dashboard content */}
      <div className="px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">My Dashboard</h2>
          
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center">
                <div className="p-3 bg-blue-500 rounded-full mr-4">
                  <FaBook className="text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Enrolled Courses</h3>
                  <p className="text-2xl font-bold text-blue-600">{liveClassContents.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <div className="flex items-center">
                <div className="p-3 bg-green-500 rounded-full mr-4">
                  <FaVideo className="text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Enrolled Batches</h3>
                  <p className="text-2xl font-bold text-green-600">{enrolledBatchIds.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
              <div className="flex items-center">
                <div className="p-3 bg-purple-500 rounded-full mr-4">
                  <FaUserTie className="text-white text-lg" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">My Teachers</h3>
                  <p className="text-2xl font-bold text-purple-600">{allTeachers.length}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Teacher Cards */}
          {allTeachers.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">My Teachers</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allTeachers.map(teacher => (
                  <div key={teacher.u_id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition">
                  <div className="bg-blue-50 p-3 flex items-center">
                    <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center text-xl font-bold mr-3">
                      {teacher.f_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 className="text-md font-medium text-gray-800">{teacher.f_name}</h4>
                      <p className="text-xs text-gray-600">Teacher</p>
                    </div>
                  </div>
                </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Classes Table Section */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">My Classes</h2>
            
            {/* Info Note */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> Live classes will become visible and accessible once the class session is live.
                  </p>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      #
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                      Class Title
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                      Date & Time
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Subject
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                      Instructor
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {combinedClasses.length > 0 ? (
                    combinedClasses.map((classItem, index) => {
                      const isLive = classItem.status === 'LIVE' && isLiveNow(classItem.dateTime, classItem.endTime);
                      
                      return (
                        <tr key={classItem.id} className={isLive ? 'bg-green-50' : ''}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 w-16">
                            {index + 1}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap w-64">
                            <div className="flex items-center">
                              <FaVideo className="text-gray-400 mr-2" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {classItem.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {classItem.title}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap w-48">
                            <div className="text-sm text-gray-800 font-medium">{formatDate(classItem.dateTime)}</div>
                            <div className="text-xs text-gray-500">{formatTime(classItem.dateTime)}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap w-32">
                            <div className="flex items-center">
                              <FaBook className="text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{classItem.subject}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap w-40">
                            <div className="flex items-center">
                              <FaUserTie className="text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{classItem.instructor}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap w-28">
                            {isLive ? (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                LIVE
                              </span>
                            ) : (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                RECORDED
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium w-40">
                            {classItem.status === 'LIVE' && !isLiveNow(classItem.dateTime, classItem.endTime) ? (
                              <button
                                onClick={() => handleJoinLiveClass(classItem.liveClassId)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                              >
                                <FaPlay className="mr-1.5" />
                                Join Live
                              </button>
                            ) : classItem.status === 'RECORDED' && classItem.recordings && classItem.recordings.length > 0 ? (
                              <a
                                href={classItem.recordings[0].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                              >
                                <FaPlay className="mr-1.5" />
                                Watch Recording
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">No recording available</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <FaVideo className="text-5xl text-gray-300 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Classes Available</h3>
                          <p className="text-gray-500">There are no live classes or recordings available at the moment.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;